import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const CartContext = createContext(null);

// A new version clears carts saved with imprecise decimal quantities.
const CART_VERSION = 9;
const CART_KEY = 'borgese_cart';
const CART_VERSION_KEY = 'borgese_cart_version';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const isWeightUnit = unit => String(unit || '').toLowerCase() === 'kg';

// Weight is always normalized through integer hundreds of grams.
const normalizeQuantity = (value, unit, allowZero = false) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return isWeightUnit(unit) ? (allowZero ? 0 : 0.1) : (allowZero ? 0 : 1);

  if (isWeightUnit(unit)) {
    const grams = clamp(Math.round((numeric * 1000) / 100) * 100, allowZero ? 0 : 100, 50000);
    return Number((grams / 1000).toFixed(1));
  }

  return clamp(Math.round(numeric), allowZero ? 0 : 1, 100);
};

const roundMoneyValue = (value, step = 0.10) => {
  const numeric = Number(value) || 0;
  const increment = Number(step) || 0;
  if (increment <= 0) return Math.round((numeric + Number.EPSILON) * 100) / 100;
  return Math.round((Math.round(numeric / increment) * increment + Number.EPSILON) * 100) / 100;
};

const itemKey = item => item.cart_item_id || (item.variant_id ? `${item.product_id}::${item.variant_id}` : item.product_id);

export function CartProvider({ children }) {
  const [cart, setCart] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [roundingStep, setRoundingStep] = useState(0.10);

  useEffect(() => {
    let cancelled = false;

    const initializeCart = async () => {
      try {
        const savedVersion = localStorage.getItem(CART_VERSION_KEY);
        if (savedVersion !== String(CART_VERSION)) {
          localStorage.removeItem(CART_KEY);
          localStorage.setItem(CART_VERSION_KEY, String(CART_VERSION));
        }

        const savedCartRaw = savedVersion === String(CART_VERSION)
          ? localStorage.getItem(CART_KEY)
          : null;
        const savedCart = savedCartRaw ? JSON.parse(savedCartRaw) : [];

        const response = await axios.get(`${BACKEND_URL}/api/products`);
        const productMap = new Map(
          (response.data || [])
            .filter(product => product?.id && product.available !== false)
            .map(product => [product.id, product])
        );

        const validated = Array.isArray(savedCart)
          ? savedCart
              .filter(item => productMap.has(item.product_id) && Number(item.quantity) > 0)
              .map(item => {
                const product = productMap.get(item.product_id);
                const variant = (product.variants || []).find(option => option.id === item.variant_id);
                const unit = product.unit || 'kg';
                const quantity = normalizeQuantity(item.quantity, unit);
                const price = Number(variant?.price_override ?? product.price_per_unit) || 0;

                return {
                  ...item,
                  cart_item_id: itemKey(item),
                  product_name: product.name,
                  variant_name: variant?.name || item.variant_name || null,
                  unit,
                  quantity,
                  price_per_unit: price,
                  total_price: roundMoneyValue(quantity * price, roundingStep)
                };
              })
          : [];

        if (!cancelled) setCart(validated);
      } catch (error) {
        console.error('Errore inizializzazione carrello:', error);
        if (!cancelled) setCart([]);
      } finally {
        if (!cancelled) setIsInitialized(true);
      }
    };

    initializeCart();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
    localStorage.setItem(CART_VERSION_KEY, String(CART_VERSION));
  }, [cart, isInitialized]);

  const addToCart = useCallback((product, requestedQuantity = product?.min_quantity || 1, selectedVariant = null) => {
    if (!product?.id) return;

    const variantId = selectedVariant?.id || null;
    const cartItemId = variantId ? `${product.id}::${variantId}` : product.id;
    const unit = product.unit || 'kg';
    const quantityToAdd = normalizeQuantity(requestedQuantity, unit);
    const price = Number(selectedVariant?.price_override ?? product.price_per_unit) || 0;

    setCart(previous => {
      const existing = previous.find(item => itemKey(item) === cartItemId);
      if (existing) {
        const quantity = isWeightUnit(unit)
          ? normalizeQuantity(Number(existing.quantity) + quantityToAdd, unit)
          : clamp(Math.round(Number(existing.quantity) || 0) + quantityToAdd, 1, 100);

        return previous.map(item => itemKey(item) === cartItemId
          ? {
              ...item,
              quantity,
              price_per_unit: price,
              total_price: roundMoneyValue(quantity * price, roundingStep)
            }
          : item
        );
      }

      return [...previous, {
        product_id: product.id,
        cart_item_id: cartItemId,
        product_name: product.name,
        variant_id: variantId,
        variant_name: selectedVariant?.name || null,
        quantity: quantityToAdd,
        unit,
        price_per_unit: price,
        total_price: roundMoneyValue(quantityToAdd * price, roundingStep),
        notes: ''
      }];
    });
  }, [roundingStep]);

  const updateQuantity = useCallback((cartItemId, requestedQuantity) => {
    setCart(previous => {
      const current = previous.find(item => itemKey(item) === cartItemId);
      if (!current) return previous;

      const numeric = Number(requestedQuantity);
      if (!Number.isFinite(numeric) || numeric <= 0) {
        return previous.filter(item => itemKey(item) !== cartItemId);
      }

      const quantity = normalizeQuantity(numeric, current.unit);
      return previous.map(item => itemKey(item) === cartItemId
        ? {
            ...item,
            quantity,
            total_price: roundMoneyValue(quantity * (Number(item.price_per_unit) || 0), roundingStep)
          }
        : item
      );
    });
  }, [roundingStep]);

  const changeQuantityByStep = useCallback((cartItemId, direction) => {
    const sign = direction < 0 ? -1 : 1;

    setCart(previous => {
      const current = previous.find(item => itemKey(item) === cartItemId);
      if (!current) return previous;

      let nextQuantity;
      if (isWeightUnit(current.unit)) {
        const currentGrams = Math.round((Number(current.quantity) || 0) * 1000);
        const nextGrams = clamp(currentGrams + sign * 100, 0, 50000);
        if (nextGrams === 0) return previous.filter(item => itemKey(item) !== cartItemId);
        nextQuantity = Number((nextGrams / 1000).toFixed(1));
      } else {
        nextQuantity = clamp(Math.round(Number(current.quantity) || 0) + sign, 0, 100);
        if (nextQuantity === 0) return previous.filter(item => itemKey(item) !== cartItemId);
      }

      return previous.map(item => itemKey(item) === cartItemId
        ? {
            ...item,
            quantity: nextQuantity,
            total_price: roundMoneyValue(nextQuantity * (Number(item.price_per_unit) || 0), roundingStep)
          }
        : item
      );
    });
  }, [roundingStep]);

  const removeFromCart = useCallback(cartItemId => {
    setCart(previous => previous.filter(item => itemKey(item) !== cartItemId));
  }, []);

  const updateItemNotes = useCallback((cartItemId, notes) => {
    setCart(previous => previous.map(item => itemKey(item) === cartItemId ? { ...item, notes } : item));
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    localStorage.removeItem(CART_KEY);
  }, []);

  const refreshCart = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/products`);
      const productMap = new Map(
        (response.data || [])
          .filter(product => product?.id && product.available !== false)
          .map(product => [product.id, product])
      );

      setCart(previous => previous
        .filter(item => productMap.has(item.product_id))
        .map(item => {
          const product = productMap.get(item.product_id);
          const variant = (product.variants || []).find(option => option.id === item.variant_id);
          const unit = product.unit || 'kg';
          const quantity = normalizeQuantity(item.quantity, unit);
          const price = Number(variant?.price_override ?? product.price_per_unit) || 0;
          return {
            ...item,
            product_name: product.name,
            variant_name: variant?.name || item.variant_name || null,
            unit,
            quantity,
            price_per_unit: price,
            total_price: roundMoneyValue(quantity * price, roundingStep)
          };
        })
      );
      return true;
    } catch (error) {
      console.error('Errore aggiornamento carrello:', error);
      return false;
    }
  }, [roundingStep]);

  const cartTotal = useMemo(
    () => roundMoneyValue(cart.reduce((sum, item) => sum + (Number(item.total_price) || 0), 0), roundingStep),
    [cart, roundingStep]
  );
  const cartItemsCount = cart.length;
  const roundMoney = useCallback(value => roundMoneyValue(value, roundingStep), [roundingStep]);

  const value = useMemo(() => ({
    cart,
    setCart,
    addToCart,
    updateQuantity,
    changeQuantityByStep,
    removeFromCart,
    updateItemNotes,
    clearCart,
    refreshCart,
    cartTotal,
    cartItemsCount,
    isInitialized,
    roundingStep,
    setRoundingStep,
    roundMoney,
    normalizeQuantity
  }), [
    cart,
    addToCart,
    updateQuantity,
    changeQuantityByStep,
    removeFromCart,
    updateItemNotes,
    clearCart,
    refreshCart,
    cartTotal,
    cartItemsCount,
    isInitialized,
    roundingStep,
    roundMoney
  ]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart deve essere usato dentro CartProvider');
  return context;
}
